/* eslint-disable camelcase */
import React from 'react'
import { Checkbox, Col, Row, Select, Typography } from 'antd'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { ImageUploader, RichTextEditorWithCharCount } from '~components'
import { ALERT_TYPES } from '~constants'
import { DatePicker, Input, Spinner } from '~stories'

import styles from './AlertInfo.module.css'

const { Option } = Select
const { Title } = Typography

const AlertInfo = ({
  allVenues = [],
  alert,
  existingAlerts = [],
  handleUrlUpdate,
  handleIconChange,
  handleMessageChange,
  handleTypeChange,
  handleImageUrlUpdate,
  handleStartDateChange,
  handleEndDateChange,
  handleCookieExpirationChange,
  handleVenueChange,
  handleShowAlertOnPromoChange
}) => {
  const {
    icon: { url: image_url, alt: image_alt = '' } = {},
    message,
    url,
    // type,
    color,
    venue_id,
    schedule: { start_date, end_date } = {},
    cookie_expiration_date,
    show_alert_on_promo = false
  } = alert ?? {}

  // Get venue timezone
  const selectedVenue = allVenues.find(v => v.value === venue_id)
  const venueTimezone = selectedVenue?.timezone || 'America/New_York'

  // Convert dates to moment objects for DatePicker
  // Handle both Unix timestamps (numbers) and date strings for backward compatibility
  const startDateMoment = start_date
    ? (typeof start_date === 'number'
      ? moment.unix(start_date).tz(venueTimezone)
      : moment(start_date).tz(venueTimezone))
    : null
  const endDateMoment = end_date
    ? (typeof end_date === 'number'
      ? moment.unix(end_date).tz(venueTimezone)
      : moment(end_date).tz(venueTimezone))
    : null
  // Convert Unix timestamp to moment object
  const cookieExpirationMoment = cookie_expiration_date
    ? (typeof cookie_expiration_date === 'number'
      ? moment.unix(cookie_expiration_date).tz(venueTimezone)
      : moment(cookie_expiration_date).tz(venueTimezone))
    : null

  // Validation: Check for overlapping dates
  const checkDateOverlap = (newStartDate, newEndDate, currentAlertId) => {
    // If no dates set, no overlap (will display immediately)
    if (!newStartDate && !newEndDate) return false

    // Convert to moment objects in venue timezone
    const newStart = newStartDate
      ? (moment.isMoment(newStartDate) ? newStartDate.clone() : moment(newStartDate).tz(venueTimezone))
      : moment().tz(venueTimezone)
    const newEnd = newEndDate
      ? (moment.isMoment(newEndDate) ? newEndDate.clone() : moment(newEndDate).tz(venueTimezone))
      : null

    return existingAlerts.some(existingAlert => {
      // Skip current alert being edited
      if (currentAlertId && existingAlert._id === currentAlertId) return false

      // Skip inactive alerts
      if (!existingAlert.active) return false

      // Only compare alerts with the same venue_id and type
      if (venue_id && existingAlert.venue_id !== venue_id) return false
      if (color && existingAlert.color !== color) return false

      // Skip alerts without dates (they display immediately and indefinitely, so they overlap with everything)
      const existingStartDate = existingAlert.schedule?.start_date || existingAlert.start_date
      const existingEndDate = existingAlert.schedule?.end_date || existingAlert.end_date
      if (!existingStartDate && !existingEndDate) return true

      const existingTimezone = existingAlert.timezone || venueTimezone
      const existingStart = existingStartDate
        ? (typeof existingStartDate === 'number'
          ? moment.unix(existingStartDate).tz(existingTimezone)
          : moment(existingStartDate).tz(existingTimezone))
        : moment().tz(existingTimezone)
      const existingEnd = existingEndDate
        ? (typeof existingEndDate === 'number'
          ? moment.unix(existingEndDate).tz(existingTimezone)
          : moment(existingEndDate).tz(existingTimezone))
        : null

      // Check overlap: two ranges overlap if start1 <= end2 AND start2 <= end1
      // For infinite ranges (null end), treat as infinite future
      // Use day-level comparison to handle showTime properly
      const newStartDay = newStart.clone().startOf('day')
      const newEndDay = newEnd ? newEnd.clone().endOf('day') : null
      const existingStartDay = existingStart.clone().startOf('day')
      const existingEndDay = existingEnd ? existingEnd.clone().endOf('day') : null

      // Check if ranges overlap at day level
      const newStartsBeforeExistingEnds = existingEndDay
        ? newStartDay.isSameOrBefore(existingEndDay)
        : true
      const existingStartsBeforeNewEnds = newEndDay
        ? existingStartDay.isSameOrBefore(newEndDay)
        : true

      return newStartsBeforeExistingEnds && existingStartsBeforeNewEnds
    })
  }

  // Disable dates that would cause overlap
  const disabledStartDate = (current) => {
    if (!current) return false

    // Disable dates that are before or equal to the current time
    const now = moment().tz(venueTimezone)
    if (current.isBefore(now, 'minute') || current.isSame(now, 'minute')) {
      return true
    }

    // Normalize current to start of day for day-level comparison
    const currentDay = current.clone().startOf('day')

    // Disable dates that are after end date (if end date is selected)
    // Compare at day level: if end date exists, disable dates on or after that day
    if (endDateMoment) {
      const endDay = endDateMoment.clone().startOf('day')
      if (currentDay.isSameOrAfter(endDay)) {
        return true
      }
    }

    // Check for overlap with existing alerts
    // Test with current day as start and endDateMoment (or null) as end
    const testEnd = endDateMoment || null
    // Use start of day for the test start to match day-level selection
    const testStart = currentDay.clone()
    return checkDateOverlap(testStart, testEnd, alert?._id)
  }

  const disabledEndDate = (current) => {
    if (!current) return false

    // Normalize current to start of day for day-level comparison
    const currentDay = current.clone().startOf('day')

    // Disable dates that are before start date (if start date is selected)
    // Compare at day level: if start date exists, disable dates strictly before that day
    // Allow the same day since showTime allows selecting a later time on the same day
    if (startDateMoment) {
      const startDay = startDateMoment.clone().startOf('day')
      if (currentDay.isBefore(startDay)) {
        return true
      }
    }

    // Check for overlap with existing alerts
    // Test with startDateMoment (or current moment) as start and current day as end
    const testStart = startDateMoment || moment().tz(venueTimezone).startOf('day')
    // Use start of day for the test end to match day-level selection
    const testEnd = currentDay.clone()
    return checkDateOverlap(testStart, testEnd, alert?._id)
  }

  // Disable past dates for cookie expiration
  const disabledCookieExpirationDate = (current) => {
    if (!current) return false
    const now = moment().tz(venueTimezone)
    // Disable dates that are before the current date/time
    // Compare at minute level to ensure past times are disabled
    return current.isBefore(now, 'minute')
  }

  // Disable "now" button for start date if it would cause issues
  const disabledStartTime = (current) => {
    if (!current) return {}
    const now = moment().tz(venueTimezone)
    const currentDay = current.clone().startOf('day')
    const nowDay = now.clone().startOf('day')

    // If selecting today, check if "now" time would be valid
    if (currentDay.isSame(nowDay, 'day')) {
      const nowHour = now.hour()
      const nowMinute = now.minute()

      // Check if "now" would be after end date
      if (endDateMoment && now.isAfter(endDateMoment)) {
        return {
          disabledHours: () => [nowHour],
          disabledMinutes: (selectedHour) => selectedHour === nowHour ? [nowMinute] : []
        }
      }

      // Check if "now" would cause overlap
      const testEnd = endDateMoment || null
      if (checkDateOverlap(now, testEnd, alert?._id)) {
        return {
          disabledHours: () => [nowHour],
          disabledMinutes: (selectedHour) => selectedHour === nowHour ? [nowMinute] : []
        }
      }
    }
    return {}
  }

  // Disable "now" button for end date if it would cause issues
  const disabledEndTime = (current) => {
    if (!current) return {}
    const now = moment().tz(venueTimezone)
    const currentDay = current.clone().startOf('day')
    const nowDay = now.clone().startOf('day')

    // If selecting today, check if "now" time would be valid
    if (currentDay.isSame(nowDay, 'day')) {
      const nowHour = now.hour()
      const nowMinute = now.minute()

      // Check if "now" would be before start date
      if (startDateMoment && now.isBefore(startDateMoment)) {
        return {
          disabledHours: () => [nowHour],
          disabledMinutes: (selectedHour) => selectedHour === nowHour ? [nowMinute] : []
        }
      }

      // Check if "now" would cause overlap
      const testStart = startDateMoment || now
      if (checkDateOverlap(testStart, now, alert?._id)) {
        return {
          disabledHours: () => [nowHour],
          disabledMinutes: (selectedHour) => selectedHour === nowHour ? [nowMinute] : []
        }
      }
    }
    return {}
  }

  // Show spinner if allVenues is not loaded
  if (!Array.isArray(allVenues) || allVenues.length === 0) {
    return (
      <div className={styles.alertInfoContainer}>
        <Spinner isLoading={true} />
      </div>
    )
  }

  console.log(alert)
  return (
    <div className={styles.alertInfoContainer}>
      {/* Basic Information Section */}
      <div className={styles.formSection}>
        <div className={styles.formField}>
          <label htmlFor="venueSelect" style={{ marginBottom: 8, paddingTop: 1, display: 'block', fontSize: 15, fontWeight: 500 }}>Venue</label>
          <Select
            id="venueSelect"
            onChange={value => handleVenueChange(value)}
            placeholder="Select a venue"
            style={{ width: '100%' }}
            value={venue_id}
          >
            {Array.isArray(allVenues) && allVenues.map(venue => (
              <Option key={venue.value} value={venue.value}>
                {venue.label}
              </Option>
            ))}
          </Select>
        </div>

        <div className={styles.formField}>
          <RichTextEditorWithCharCount label='Message' onChange={handleMessageChange} value={message}/>
        </div>
        <div className={styles.formField}>
          <label style={{ marginBottom: 12, display: 'block', fontSize: 15, fontWeight: 500 }}>Alert Type</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            {ALERT_TYPES.map(alertType => {
              const isSelected = (color || ALERT_TYPES[0].color) === alertType.color
              return (
                <div
                  key={alertType.color}
                  onClick={() => handleTypeChange(alertType.color)}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    backgroundColor: alertType.color,
                    border: isSelected ? '3px solid #1890ff' : '2px solid #e0e0e0',
                    boxShadow: isSelected ? '0 0 0 3px rgba(24, 144, 255, 0.25)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {isSelected && (
                    <span style={{ color: '#fff', fontSize: 18, fontWeight: 'bold', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>✓</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className={styles.formField}>
          <Input label='Url' onChange={handleUrlUpdate} value={url}/>
        </div>

        <div className={styles.formField}>
          <Checkbox
            checked={show_alert_on_promo}
            onChange={handleShowAlertOnPromoChange}
          >
            Enable alert on promotions
          </Checkbox>
        </div>
      </div>

      {/* Scheduling Section */}
      <div className={styles.formSection}>
        <Row align="left" className={styles.titleWrapper}>
          <Col>
            <Title className={styles.sectionTitle} level={3}>Scheduling</Title>
          </Col>
        </Row>
        <Row gutter={[24, 24]}>
          <Col md={12} sm={24} xs={24}>
            <div className={styles.formField}>
              <DatePicker
                allowClear
                disabledDate={disabledStartDate}
                disabledTime={disabledStartTime}
                format="YYYY-MM-DD h:mm A"
                label="Start Date (Optional)"
                labelClassName=""
                onChange={handleStartDateChange}
                showTime
                value={startDateMoment}
              />
              <div className={styles.helperText}>
                {!start_date && 'If not set, alert will display immediately'}
              </div>
            </div>
          </Col>
          <Col md={12} sm={24} xs={24}>
            <div className={styles.formField}>
              <DatePicker
                allowClear
                disabledDate={disabledEndDate}
                disabledTime={disabledEndTime}
                format="YYYY-MM-DD h:mm A"
                label="End Date (Optional)"
                labelClassName=""
                onChange={handleEndDateChange}
                showTime
                value={endDateMoment}
              />
              <div className={styles.helperText}>
                {!end_date && 'If not set, alert must be manually disabled to remove'}
              </div>
            </div>
          </Col>
        </Row>
        <div className={styles.timezoneInfo} />
        <div className={styles.formField}>
          <DatePicker
            allowClear
            disabledDate={disabledCookieExpirationDate}
            format="YYYY-MM-DD h:mm A"
            label="Cookie Expiration Time (Optional)"
            labelClassName=""
            onChange={(date) => {
              // Interpret the selected date/time as being in the venue timezone, then convert to Unix timestamp
              if (date) {
                // Create a moment in the venue timezone with the same date/time values
                const dateInVenueTz = moment.tz(
                  date.format('YYYY-MM-DD HH:mm:ss'),
                  venueTimezone
                )
                handleCookieExpirationChange(dateInVenueTz)
              } else {
                handleCookieExpirationChange(null)
              }
            }}
            showTime
            value={cookieExpirationMoment}
          />
          <div className={styles.helperText}>
            {!cookie_expiration_date && 'If empty, cookie is cleared when cache is cleared'}
          </div>
        </div>

        {selectedVenue && (
          <div className={styles.timezoneInfo}>
              Timezone: {venueTimezone} (based on selected venue)
          </div>
        )}
      </div>

      {/* Media Section */}
      <div className={styles.formSection}>
        <div className={styles.formField}>
          <label htmlFor="defaultImageUploader" style={{ marginBottom: 8, paddingTop: 1, display: 'block', fontSize: 15, fontWeight: 500 }}>Icon</label>
          <ImageUploader
            alt={image_alt || ''}
            handleIconChange={imageObj => handleIconChange(imageObj)}
            handleUpload={imageUrl => handleImageUrlUpdate(imageUrl)}
            logoUrl={image_url || ''}
          />
        </div>
      </div>
    </div>
  )
}

AlertInfo.propTypes = {
  alert: PropTypes.object.isRequired,
  allVenues: PropTypes.array,
  existingAlerts: PropTypes.array,
  handleCookieExpirationChange: PropTypes.func.isRequired,
  handleEndDateChange: PropTypes.func,
  handleIconChange: PropTypes.func.isRequired,
  handleImageUrlUpdate: PropTypes.func.isRequired,
  handleMessageChange: PropTypes.func.isRequired,
  handleShowAlertOnPromoChange: PropTypes.func,
  handleStartDateChange: PropTypes.func,
  handleTypeChange: PropTypes.func,
  handleUrlUpdate: PropTypes.func.isRequired,
  handleVenueChange: PropTypes.func.isRequired
}

AlertInfo.defaultProps = {
  allVenues: [],
  existingAlerts: [],
  handleCookieExpirationChange: () => {},
  handleEndDateChange: () => {},
  handleIconChange: () => {},
  handleImageUrlUpdate: () => {},
  handleStartDateChange: () => {},
  handleVenueChange: () => {},
  handleShowAlertOnPromoChange: () => {}
}

export default AlertInfo
